/**
 * The pure half of a sheet promotion (`LonaSdk.Client`'s ephemeral → server
 * promote): the shapes a promote's create and nested writes answer, the
 * structural correlation of a recovered server sheet with the rows the
 * create sent, and the checked write a required promote step goes through.
 * No state: the promotion methods on the client own the requests.
 */
import type { Option } from "@tento-lona/sheets";
import type { Rational } from "@tento-core/rational";
import type { ResponseError } from "@tento-core/typed-api";
import type { Result } from "@tento-core/result";
import { RowKey, type RowId } from "@tento-lona/sheets";
import { elementAt } from "@tento-lona/sheets/internal-sdk";
import { Network } from "../../network.ts";
import { PromoteWriteError, SdkRequestError, type SdkRequestFailure } from "../../errors.ts";

export type PromotionNewUserSheet = Network.NewUserSheet & {
  readonly lookupKey?: string;
  readonly timeScale?: Network.TimeScale;
};

/**
 * A caller-owned row identity for a create; an explicit owner scope is a data
 * address, not a reusable shim identity. Mirrors the server create boundary,
 * including its legacy colon-prefixed spelling. The local read key is intact.
 */
export function promotionRowLookupKey(lookupKey: Option<string>): Option<string> {
  return lookupKey?.startsWith("[") || lookupKey?.startsWith(":[") ? null : lookupKey;
}

/**
 * HTTP statuses that mean "this sheet id IS persisted, but not for you".
 * The sheets route answers a cross-principal read with an auth rejection
 * rather than a miss, so these are the only codes that distinguish a
 * SQUATTED id from a sheet that simply isn't there.
 */
export const CROSS_OWNER_SHEET_READ_STATUSES: readonly number[] = [401, 403];

/**
 * Outcome of the duplicate-recovery read that follows a
 * `spreadsheets_pkey` create failure — see `loadExistingPromotedSheet`.
 *
 * - `sheet` non-null: the id is readable here, so the duplicate is this
 *   principal's own earlier promote and the promote recovers by adopting it.
 * - `crossOwner`: the read was REJECTED (401/403). The id belongs to another
 *   principal; it can never be claimed by this session, so "duplicate ⇒
 *   readable" is a false recovery.
 * - neither: a plain miss (404 / empty body) — the pre-existing hard failure.
 */
export type ExistingPromotedSheet = {
  readonly sheet: Option<Network.UserSheetWithRows>;
  readonly crossOwner: boolean;
  readonly status: Option<number>;
  /** The key form (`ss_<hex>` or the bare uuid) whose answer `status` is —
   *  the path a refused read-back reports (`PromoteWriteError` step `get`). */
  readonly sheetKey: string;
};

/**
 * What the promote's `POST /api/sheets` produced. The route is
 * `create_with_rows_idempotent` (the server's sheet-create route), and it answers
 * HTTP 200 in two different ways with the same body shape and no flag:
 *
 * - a FRESH create: the sent roots, one row per sent row, in send order;
 * - an id the caller ALREADY owns: the stored sheet with every stored row,
 *   children included, in no particular order (`find_all_with_sheet` has no
 *   ORDER BY) — a re-promote from a fresh session, or a retry after a lost
 *   create response.
 *
 * So HTTP success proves nothing about the row list. `adopted` is false only
 * when the answer IS the sent roots, index by index
 * (`isFreshCreateResponse`). Otherwise — that reuse, a legacy duplicate-key
 * 500 recovered by a read, or a retry resuming its own earlier attempt — the
 * rows are whatever an earlier attempt stored, possibly only SOME of the
 * roots (`create_with_rows` saves the sheet, then adds the roots one by one,
 * outside one transaction), and the promote correlates them structurally
 * (`correlateRecoveredRows`) and writes what is missing
 * (`reconcilePromotedRows`), never by index.
 */
export type PromotedSheet = {
  readonly sheet: Network.UserSheetWithRows;
  readonly adopted: boolean;
};

/**
 * The server rows a promote persisted beyond the create — every nested row,
 * with each parent's `children` filled in — plus the counts the
 * `cow-promote-hydrate` trace reports.
 */
export type PromotedChildren = {
  readonly rows: Network.SheetRow[];
  readonly parents: number;
  readonly hydrated: number;
  readonly reparented: number;
  readonly patched: number;
  /** Nested rows an earlier attempt already stored in place (no write needed). */
  readonly alreadyPersisted: number;
  /** Nested rows left unwritten because their parent has no server row. */
  readonly unresolved: number;
  /** Rows the create did not store, added one by one (`POST /api/sheets/{sheet}/row`). */
  readonly rowsAdded: number;
  /**
   * Nested rows re-created under a parent the server had already hydrated
   * (the `children_hydrated` fast path answers existing children and creates
   * nothing): added, then re-parented.
   */
  readonly readded: number;
  /**
   * Local keys of mapped rows whose server row, after every write, still sits
   * under the wrong parent / at the wrong position or lacks its corrected
   * metadata (the tree-edge half of the completeness check).
   */
  readonly misplaced: readonly string[];
};

/**
 * The server rows a promote's nested writes reconcile against: every stored
 * row by canonical key, and each stored parent's children. A fresh create's is
 * its own response (the roots, no children); a resumed or adopted promote's
 * is the sheet as the server holds it now.
 */
export type PromoteServerTree = {
  readonly rows: ReadonlyMap<string, Network.SheetRow>;
  readonly childrenOf: ReadonlyMap<string, readonly string[]>;
};

export function promoteServerTree(
  rows: readonly Network.SheetRow[],
): PromoteServerTree {
  const byKey = new Map<string, Network.SheetRow>();
  const childrenOf = new Map<string, string[]>();
  for (const row of rows) {
    const key = promotionRowKey(row.id);
    byKey.set(key, row);
    const kids = (row.children ?? []).map((c) => promotionRowKey(c));
    if (kids.length > 0) childrenOf.set(key, kids);
  }
  return { rows: byKey, childrenOf };
}

/** Whether two wire positions name the same rational (`1/2` = `2/4`); a missing one matches nothing. */
export function samePromotedPosition(
  a: Option<{ readonly n: number; readonly d: number }>,
  b: Option<{ readonly n: number; readonly d: number }>,
): boolean {
  if (a == null || b == null) return false;
  return a.n * b.d === b.n * a.d;
}

/**
 * Whether a successful `POST /api/sheets` answer is a FRESH create of `sent`:
 * exactly one row per sent row, index by index at the sent position with the
 * sent type (a lookup-keyed row may come back as the user's reused row, so
 * only its position is compared), no children, no id twice. Anything else is
 * `create_with_rows_idempotent` handing back a sheet the caller already owns
 * (see `PromotedSheet`), which must be reconciled, not read positionally.
 * A stored sheet that happens to list exactly the sent roots in send order
 * reads as fresh — and the positional correlation is then the structural one.
 */
export function isFreshCreateResponse(
  sent: readonly Network.NewRowWithAttributes[],
  returned: readonly Network.SheetRow[],
): boolean {
  if (returned.length !== sent.length) return false;
  const seen = new Set<string>();
  for (let i = 0; i < sent.length; i++) {
    const row = elementAt(returned, i, "isFreshCreateResponse: returned row");
    const sentRow = elementAt(sent, i, "isFreshCreateResponse: sent row");
    const key = promotionRowKey(row.id);
    if (seen.has(key)) return false;
    seen.add(key);
    if ((row.children ?? []).length > 0) return false;
    if (!samePromotedPosition(row.position, sentRow.position ?? null)) {
      return false;
    }
    if (sentRow.lookupKey == null && row.type !== sentRow.type) return false;
  }
  return true;
}

/**
 * Correlate the rows a promote's create sent (`created`, with the positions it
 * sent) with a RECOVERED server sheet — the idempotent create handing back an
 * existing sheet, a duplicate-create adoption or a resumed retry. Never by
 * index: the recovered sheet lists every stored row in no order, nested ones
 * included. An unscoped lookup-keyed row is found by its
 * lookup key (it may already be re-parented); scoped data keys identify fresh
 * shims whose stored lookup key is null. These and other anonymous rows match
 * only anonymous stored roots by type +
 * sent position among the stored ROOTS, preferring a stored row with the same
 * label (a nested row an earlier attempt added as a root and never re-parented
 * can share a root's type + position; it must not be taken for that root —
 * `reconcilePromotedRows` re-parents it). A row already in `serverIdOf` (the
 * earlier attempt's own record) keeps its mapping. A row with no match is left
 * unmapped and counted — the stored sheet does not hold it, and
 * `reconcilePromotedRows` adds it.
 */
export function correlateRecoveredRows(
  created: readonly Network.SheetRow[],
  sentPositions: readonly Option<Rational>[],
  server: PromoteServerTree,
  serverIdOf: Map<string, string>,
): { readonly matched: number; readonly unmatched: number } {
  const nested = new Set<string>();
  for (const kids of server.childrenOf.values()) {
    for (const kid of kids) nested.add(kid);
  }
  const claimed = new Set<string>(serverIdOf.values());
  let matched = 0;
  let unmatched = 0;
  for (let i = 0; i < created.length; i++) {
    const row = elementAt(created, i, "correlateRecoveredRows: created row");
    const localKey = promotionRowKey(row.id);
    if (serverIdOf.has(localKey)) {
      matched++;
      continue;
    }
    const sent = sentPositions[i] ?? null;
    const lookupKey = promotionRowLookupKey(row.lookupKey);
    let found: Option<string> = null;
    for (const sameLabel of [true, false]) {
      for (const [key, stored] of server.rows) {
        if (claimed.has(key)) continue;
        if (sameLabel && (stored.label ?? null) !== (row.label ?? null)) {
          continue;
        }
        const hit = lookupKey != null
          ? stored.lookupKey === lookupKey
          : stored.lookupKey == null && !nested.has(key) && stored.type === row.type &&
            samePromotedPosition(stored.position, sent);
        if (hit) {
          found = key;
          break;
        }
      }
      if (found != null) break;
    }
    if (found == null) {
      unmatched++;
      continue;
    }
    serverIdOf.set(localKey, found);
    claimed.add(found);
    matched++;
  }
  return { matched, unmatched };
}

/** The promote writes that must be confirmed before the handle is synced. */
type PromoteWriteStep = PromoteWriteError.Step;

/**
 * The outcome and HTTP status a failed create carries. `SheetsClient.create`
 * throws `SheetWriteError` (an `SdkRequestError`) and keeps both; any other
 * error was thrown before a verdict (`"unanswered"`).
 */
export function createVerdictOf(
  err: Option<SdkRequestError>,
): SdkRequestFailure {
  return err?.failure ?? { outcome: "unanswered" };
}

/** A promote row write as the single-row add's body (`NewRowBody`: no `heightPx`). */
export function promotionRowBody(
  sent: Network.NewRowWithAttributes,
): Network.NewRowBody {
  return Network.NewRowBody.make({
    type: sent.type,
    position: sent.position,
    label: sent.label,
    formula: sent.formula,
    timeScale: sent.timeScale,
    attributes: sent.attributes,
    lookupKey: promotionRowLookupKey(sent.lookupKey),
    surface: null,
    // A promote's rows carry no client-minted id (only a pending row's persist does).
    id: null,
  });
}

/**
 * Send one required promote write through a result-returning `TypedApi` call
 * (`postResult` / `patchResult`) and unwrap it, turning a non-2xx answer or a
 * transport failure into a `PromoteWriteError`. The promote uses this instead
 * of `RowApi` so it never depends on the request-error delegate; other
 * `TypedApi` callers keep their tolerated-failure behaviour.
 */
export async function checkedPromoteWrite<T>(
  step: PromoteWriteStep,
  method: "POST" | "PATCH",
  path: string,
  send: () => Promise<Result<T, ResponseError>>,
): Promise<T> {
  let result: Result<T, ResponseError>;
  try {
    result = await send();
  } catch (err) {
    throw SdkRequestError.rejected(err, (answer) =>
      new PromoteWriteError({ step, method, path, ...SdkRequestError.Answer.fields(answer) }));
  }
  return result.unwrapOrElse((e) => {
    const verdict = SdkRequestError.answered(e.code);
    throw new PromoteWriteError({
      step,
      method,
      path,
      failure: verdict,
      reason: e.reason,
      details: e.details,
    });
  });
}

/** Canonical rows-side key for a wire/serialized row id (what `rekeyRows` matches). */
export function promotionRowKey(id: RowId | string): string {
  return RowKey.of(String(id)).toString();
}

/**
 * The serialized rows a promote does NOT persist: template-synthesized rows
 * (`virtual(r_<anchor>|<preset>)` — `:calendar`'s `calendar-body` →
 * `alias.calendar-list`) whose whole subtree is synthesized too.
 *
 * A virtual row is a pure function of its real anchor's id and its preset:
 * every load re-synthesizes it (`expandChildSpecsRecursive`), and the only
 * thing that dedupes a synthesized row against an existing one is that key
 * (`sheet.peekRow(virtualId)`). A persisted copy can never carry it — the
 * server mints a fresh `r_` id for every row it stores and keeps no
 * `clientId` — so it loaded as a SECOND, bare `calendar-body` next to the
 * synthesized one (ux-test weather-location-rebind, candidate 18). Its
 * customizations already live on the anchor (`children` / `overrides` /
 * parent-storage `nestedRows`), so nothing is lost by not sending it.
 *
 * A virtual row that holds a REAL descendant is kept (and persisted under its
 * parent as before): skipping it would strand that descendant with no server
 * parent. The `cow-promote-hydrate` trace counts both.
 */
export function templateSynthesizedRowKeys(
  rows: readonly Network.SheetRow[],
): { readonly skipped: Set<string>; readonly kept: number } {
  const childrenOf = new Map<string, string[]>();
  for (const row of rows) {
    childrenOf.set(
      promotionRowKey(row.id),
      (row.children ?? []).map((c) => promotionRowKey(c)),
    );
  }
  const verdict = new Map<string, boolean>();
  const synthesizedSubtree = (key: string): boolean => {
    const known = verdict.get(key);
    if (known != null) return known;
    let all = RowKey.of(key).isVirtual();
    for (const kid of childrenOf.get(key) ?? []) {
      if (!synthesizedSubtree(kid)) all = false;
    }
    verdict.set(key, all);
    return all;
  };
  const skipped = new Set<string>();
  let kept = 0;
  for (const key of childrenOf.keys()) {
    if (synthesizedSubtree(key)) skipped.add(key);
    else if (RowKey.of(key).isVirtual()) kept++;
  }
  return { skipped, kept };
}

/**
 * One serialized row as a promote write (`create_with_rows` or
 * `hydrate-children`). `position` is the caller's: top-level order for a
 * created root, the in-parent position for a nested row.
 */
export function promotionNewRow(
  row: Network.SheetRow,
  position: Option<Rational>,
): Network.NewRowWithAttributes {
  return Network.NewRowWithAttributes.make({
    type: row.type,
    position,
    label: row.label,
    formula: row.formula,
    heightPx: row.heightPx,
    timeScale: row.timeScale,
    attributes: row.attributes,
    lookupKey: promotionRowLookupKey(row.lookupKey),
    clientId: null,
    icon: null,
  });
}
